
import React, { useState, useEffect } from 'react';
import { useWedding } from '../context/WeddingContext';
import { useAuth } from '../context/AuthContext';
import { getTranslation } from '../i18n';
import { supabase, getAuthenticatedSupabase, isUsingRealSupabase } from '../supabaseClient';
import { Wedding, Plan, SubscriptionStatus, GuestRelationshipType } from '../types';
import { AiProviderId } from '../lib/ai/types';
import { getApiKey, setApiKey, getBaseUrl, setBaseUrl } from '../lib/ai/storage';
import { apiClient } from '../lib/api/client';
import { ALL_USERS_PRO_PROMO } from '../constants';
import { applyInvitationDefaultsToForm, getStoredPublicConfigFromWedding } from '../lib/invitation/defaults';
import { ensureDefaultGuestRelationshipTypes } from '../lib/guests/relationships';
import { buildPublicInvitationUrl } from '../lib/invitation/urls';
import {
  uploadInvitationPublicImage,
  removeInvitationPublicImageByUrl,
  type InvitationPhotoSlot,
} from '../lib/invitation/upload';
import { Modal } from '../components/Modal';
import { Toast, ToastMessage } from '../components/Toast';
import { Heart, Languages, Sun, Moon, ShieldCheck, Lock, CreditCard, Users, CheckCircle, AlertTriangle, List, Plus, X, Bot, Eye, EyeOff, Wifi, UserPlus, Mail, User as UserIcon, Pencil, Info, ChevronDown, ChevronUp, PlusCircle, Link2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { InvitationPhotoUpload } from '../components/InvitationPhotoUpload';
import {
  ACCOMMODATION_INFO_DEFAULT,
  CATERING_SECTION_INTRO_DEFAULT,
  CATERING_MAIN_BODY_DEFAULT,
  CATERING_WARM_BODY_DEFAULT,
  CATERING_COLD_BODY_DEFAULT,
  CATERING_SWEET_BODY_DEFAULT,
  CATERING_DRINKS_BODY_DEFAULT,
} from '../lib/invitation/publicInvitationDisplay';

/** Výchozí texty pro placeholder v Nastavení (stejné jako na pozvánce při prázdném poli). */
const INVITATION_CATERING_BLOCKS = [
  {
    showKey: 'cateringShowMain' as const,
    textKey: 'cateringMainText' as const,
    photoKey: 'cateringMainPhotoUrl' as const,
    slot: 'cateringMain' as const,
    label: 'Hlavní chod',
    placeholder: CATERING_MAIN_BODY_DEFAULT,
  },
  {
    showKey: 'cateringShowWarmBuffet' as const,
    textKey: 'cateringWarmBuffetText' as const,
    photoKey: 'cateringWarmPhotoUrl' as const,
    slot: 'cateringWarm' as const,
    label: 'Teplý raut',
    placeholder: CATERING_WARM_BODY_DEFAULT,
  },
  {
    showKey: 'cateringShowColdBuffet' as const,
    textKey: 'cateringColdBuffetText' as const,
    photoKey: 'cateringColdPhotoUrl' as const,
    slot: 'cateringCold' as const,
    label: 'Studený raut',
    placeholder: CATERING_COLD_BODY_DEFAULT,
  },
  {
    showKey: 'cateringShowSweetBar' as const,
    textKey: 'cateringSweetBarText' as const,
    photoKey: 'cateringSweetPhotoUrl' as const,
    slot: 'cateringSweet' as const,
    label: 'Sweet bar',
    placeholder: CATERING_SWEET_BODY_DEFAULT,
  },
  {
    showKey: 'cateringShowDrinks' as const,
    textKey: 'cateringDrinksText' as const,
    photoKey: 'cateringDrinksPhotoUrl' as const,
    slot: 'cateringDrinks' as const,
    label: 'Nápoje',
    placeholder: CATERING_DRINKS_BODY_DEFAULT,
  },
] as const;

// Wedding role definitions
const WEDDING_ROLES = [
  { key: 'WEDDING_ADMIN', label: 'Administrátor', description: 'Plný přístup ke všem sekcím, správa uživatelů a oprávnění.' },
  { key: 'COORDINATOR', label: 'Svatební koordinátor', description: 'Přístup k většině sekcí, pomáhá s organizací.' },
  { key: 'BRIDE', label: 'Nevěsta', description: 'Hlavní účastnice, přístup k plánování a úpravám.' },
  { key: 'GROOM', label: 'Ženich', description: 'Hlavní účastník, přístup k plánování a úpravám.' },
  { key: 'WITNESS', label: 'Svědek/Svědkyně', description: 'Čtení většiny sekcí, může navrhovat aktivity.' },
  { key: 'BRIDESMAID', label: 'Družička', description: 'Čtení sekcí, může navrhovat aktivity.' },
  { key: 'FAMILY_GUEST', label: 'Rodinný host', description: 'Omezený přístup ke čtení harmonogramu.' },
] as const;

// Permission matrix for display
const ROLE_PERMISSIONS_MATRIX = {
  WEDDING_ADMIN: { canRead: ['Vše'], canWrite: ['Vše'], canManageUsers: true },
  COORDINATOR: { canRead: ['Vše'], canWrite: ['Vše'], canManageUsers: false },
  BRIDE: { canRead: ['Vše'], canWrite: ['Vše'], canManageUsers: false },
  GROOM: { canRead: ['Vše'], canWrite: ['Vše'], canManageUsers: false },
  WITNESS: { canRead: ['Harmonogram', 'Zábava', 'Hosté', 'Poznámky'], canWrite: ['Zábava', 'Poznámky'], canManageUsers: false },
  BRIDESMAID: { canRead: ['Harmonogram', 'Zábava', 'Hosté'], canWrite: ['Zábava'], canManageUsers: false },
  FAMILY_GUEST: { canRead: ['Harmonogram', 'Zábava'], canWrite: [], canManageUsers: false },
};

export default function SettingsPage() {
  const { wedding, locale, theme, setLocale, setTheme, refreshWedding, updateWeddingInList } = useWedding();
  const { user, accessToken } = useAuth();
  const t = (key: string) => getTranslation(locale, key);

  const [weddingForm, setWeddingForm] = useState<Partial<Wedding>>({});
  const [activeTab, setActiveTab] = useState<'general' | 'plan' | 'team' | 'ai' | 'invitation'>('general');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // Vendor Types Management
  const [newVendorType, setNewVendorType] = useState('');

  // Guest relationship types (číselník vazeb mezi hosty)
  const [guestRelTypes, setGuestRelTypes] = useState<GuestRelationshipType[]>([]);
  const [newGuestRelLabel, setNewGuestRelLabel] = useState('');
  
  // Team members state
  type Member = { id: string; name: string; role: string; role_key?: string; email?: string; username?: string };
  const [members, setMembers] = useState<Member[]>([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role_key: 'FAMILY_GUEST',
  });
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [createdUserPassword, setCreatedUserPassword] = useState<string | null>(null);
  
  // Roles info panel state
  const [showRolesInfo, setShowRolesInfo] = useState(false);

  const [toast, setToast] = useState<ToastMessage | null>(null);
  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const [editMember, setEditMember] = useState<Member | null>(null);
  const [editForm, setEditForm] = useState({ role_key: 'FAMILY_GUEST', newPassword: '' });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Change own password
  const [changePasswordForm, setChangePasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changePasswordMessage, setChangePasswordMessage] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // AI Settings State
  const [aiKeys, setAiKeys] = useState<Record<string, string>>({
    openai: '',
    anthropic: '',
    gemini: '',
    custom: '',
    customUrl: ''
  });
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});

  // Invitation settings state
  const [invForm, setInvForm] = useState({
    public_enabled: false,
    public_slug: '',
    brideName: '',
    groomName: '',
    bridePhotoUrl: '',
    groomPhotoUrl: '',
    ceremonyTime: '',
    ceremonyVenue: '',
    fullAddress: '',
    mapsUrl: '',
    dressCode: '',
    heroImageUrl: '',
    welcomeMessage: '',
    storyText: '',
    showSchedule: true,
    showFaq: true,
    showCatering: false,
    cateringIntro: '',
    cateringShowMain: false,
    cateringMainText: '',
    cateringShowWarmBuffet: false,
    cateringWarmBuffetText: '',
    cateringShowColdBuffet: false,
    cateringColdBuffetText: '',
    cateringShowSweetBar: false,
    cateringSweetBarText: '',
    cateringShowDrinks: false,
    cateringDrinksText: '',
    cateringMainPhotoUrl: '',
    cateringWarmPhotoUrl: '',
    cateringColdPhotoUrl: '',
    cateringSweetPhotoUrl: '',
    cateringDrinksPhotoUrl: '',
    showMusic: false,
    musicIntro: '',
    musicLiveDj: false,
    musicFromSpeakers: false,
    musicSpotifyUrl: '',
    musicPlaylistCta: '',
    showEntertainment: false,
    showAccommodationInfo: false,
    accommodationInfo: '',
    accommodationMapsUrl: '',
    showGuestDetailsForm: false,
    guestFormIntro: '',
    guestFormShowAccommodation: false,
    guestFormShowAllergies: false,
    guestFormShowChildren: false,
    guestFormShowGuestNote: false,
  });
  const [isSavingInv, setIsSavingInv] = useState(false);
  const [slugCopied, setSlugCopied] = useState(false);
  const [invUploadSlot, setInvUploadSlot] = useState<InvitationPhotoSlot | null>(null);

  useEffect(() => {
    if (wedding) {
      setWeddingForm(wedding);
      const cfg = getStoredPublicConfigFromWedding(wedding) as Record<string, unknown>;
      setInvForm({
        public_enabled: wedding.public_enabled ?? false,
        public_slug: wedding.public_slug ?? '',
        brideName: (cfg.brideName as string | undefined) ?? '',
        groomName: (cfg.groomName as string | undefined) ?? '',
        bridePhotoUrl: (cfg.bridePhotoUrl as string | undefined) ?? '',
        groomPhotoUrl: (cfg.groomPhotoUrl as string | undefined) ?? '',
        ceremonyTime: (cfg.ceremonyTime as string | undefined) ?? '',
        ceremonyVenue: (cfg.ceremonyVenue as string | undefined) ?? '',
        fullAddress: (cfg.fullAddress as string | undefined) ?? '',
        mapsUrl: (cfg.mapsUrl as string | undefined) ?? '',
        dressCode: (cfg.dressCode as string | undefined) ?? '',
        heroImageUrl: (cfg.heroImageUrl as string | undefined) ?? '',
        welcomeMessage: (cfg.welcomeMessage as string | undefined) ?? wedding.welcome_message ?? '',
        storyText: (cfg.storyText as string | undefined) ?? '',
        showSchedule: (typeof cfg.showSchedule === 'boolean' ? cfg.showSchedule : undefined) ?? true,
        showFaq: (typeof cfg.showFaq === 'boolean' ? cfg.showFaq : undefined) ?? true,
        showCatering: (typeof cfg.showCatering === 'boolean' ? cfg.showCatering : undefined) ?? false,
        cateringIntro: (cfg.cateringIntro as string | undefined) ?? '',
        cateringShowMain: (typeof cfg.cateringShowMain === 'boolean' ? cfg.cateringShowMain : undefined) ?? false,
        cateringMainText: (cfg.cateringMainText as string | undefined) ?? '',
        cateringShowWarmBuffet:
          (typeof cfg.cateringShowWarmBuffet === 'boolean' ? cfg.cateringShowWarmBuffet : undefined) ?? false,
        cateringWarmBuffetText: (cfg.cateringWarmBuffetText as string | undefined) ?? '',
        cateringShowColdBuffet:
          (typeof cfg.cateringShowColdBuffet === 'boolean' ? cfg.cateringShowColdBuffet : undefined) ?? false,
        cateringColdBuffetText: (cfg.cateringColdBuffetText as string | undefined) ?? '',
        cateringShowSweetBar:
          (typeof cfg.cateringShowSweetBar === 'boolean' ? cfg.cateringShowSweetBar : undefined) ?? false,
        cateringSweetBarText: (cfg.cateringSweetBarText as string | undefined) ?? '',
        cateringShowDrinks:
          (typeof cfg.cateringShowDrinks === 'boolean' ? cfg.cateringShowDrinks : undefined) ?? false,
        cateringDrinksText: (cfg.cateringDrinksText as string | undefined) ?? '',
        cateringMainPhotoUrl: (cfg.cateringMainPhotoUrl as string | undefined) ?? '',
        cateringWarmPhotoUrl: (cfg.cateringWarmPhotoUrl as string | undefined) ?? '',
        cateringColdPhotoUrl: (cfg.cateringColdPhotoUrl as string | undefined) ?? '',
        cateringSweetPhotoUrl: (cfg.cateringSweetPhotoUrl as string | undefined) ?? '',
        cateringDrinksPhotoUrl: (cfg.cateringDrinksPhotoUrl as string | undefined) ?? '',
        showMusic: (typeof cfg.showMusic === 'boolean' ? cfg.showMusic : undefined) ?? false,
        musicIntro: (cfg.musicIntro as string | undefined) ?? '',
        musicLiveDj: (typeof cfg.musicLiveDj === 'boolean' ? cfg.musicLiveDj : undefined) ?? false,
        musicFromSpeakers: (typeof cfg.musicFromSpeakers === 'boolean' ? cfg.musicFromSpeakers : undefined) ?? false,
        musicSpotifyUrl: (cfg.musicSpotifyUrl as string | undefined) ?? '',
        musicPlaylistCta: (cfg.musicPlaylistCta as string | undefined) ?? '',
        showEntertainment: (typeof cfg.showEntertainment === 'boolean' ? cfg.showEntertainment : undefined) ?? false,
        showAccommodationInfo:
          (typeof cfg.showAccommodationInfo === 'boolean' ? cfg.showAccommodationInfo : undefined) ?? false,
        accommodationInfo: (cfg.accommodationInfo as string | undefined) ?? '',
        accommodationMapsUrl: (cfg.accommodationMapsUrl as string | undefined) ?? '',
        showGuestDetailsForm:
          (typeof cfg.showGuestDetailsForm === 'boolean' ? cfg.showGuestDetailsForm : undefined) ?? false,
        guestFormIntro: (cfg.guestFormIntro as string | undefined) ?? '',
        guestFormShowAccommodation:
          (typeof cfg.guestFormShowAccommodation === 'boolean' ? cfg.guestFormShowAccommodation : undefined) ??
          false,
        guestFormShowAllergies:
          (typeof cfg.guestFormShowAllergies === 'boolean' ? cfg.guestFormShowAllergies : undefined) ?? false,
        guestFormShowChildren:
          (typeof cfg.guestFormShowChildren === 'boolean' ? cfg.guestFormShowChildren : undefined) ?? false,
        guestFormShowGuestNote:
          (typeof cfg.guestFormShowGuestNote === 'boolean' ? cfg.guestFormShowGuestNote : undefined) ?? false,
      });
    }
    
    // Load existing keys (masked by logic, here we load actual for edit)
    setAiKeys({
      openai: getApiKey('openai'),
      anthropic: getApiKey('anthropic'),
      gemini: getApiKey('gemini'),
      custom: getApiKey('custom'),
      customUrl: getBaseUrl()
    });

    // Load wedding members
    if (wedding) {
      loadWeddingMembers();
    }
  }, [wedding]);

  /** Doplní prázdná pole pozvánky ze Svatebního týmu (hosté bride/groom) a ceremony_meta. */
  useEffect(() => {
    if (!wedding) return;
    let cancelled = false;
    const run = async () => {
      try {
        const client = getAuthenticatedSupabase();
        const [guestsRes, metaRes] = await Promise.all([
          client
            .from('guests')
            .select('full_name, role')
            .eq('wedding_id', wedding.id)
            .in('role', ['bride', 'groom']),
          client
            .from('ceremony_meta')
            .select('start_time, location_name, location_address')
            .eq('wedding_id', wedding.id)
            .maybeSingle(),
        ]);
        if (cancelled) return;
        setInvForm((prev) =>
          applyInvitationDefaultsToForm(
            prev,
            {
              title: wedding.title,
              date: wedding.date,
              location: wedding.location ?? '',
              welcome_message: wedding.welcome_message,
            },
            getStoredPublicConfigFromWedding(wedding),
            guestsRes.data ?? [],
            metaRes.data,
          ),
        );
      } catch (e) {
        console.error('Invitation defaults:', e);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [wedding?.id]);

  useEffect(() => {
    if (!wedding) return;
    let cancelled = false;
    const run = async () => {
      try {
        const client = getAuthenticatedSupabase();
        const loc = locale === 'en' ? 'en' : 'cs';
        await ensureDefaultGuestRelationshipTypes(client, wedding.id, loc);
        const { data, error } = await client
          .from('guest_relationship_types')
          .select('*')
          .eq('wedding_id', wedding.id)
          .order('sort_order');
        if (cancelled) return;
        if (!error && data) setGuestRelTypes(data);
      } catch (e) {
        console.warn('guest_relationship_types', e);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [wedding?.id, locale]);

  const loadGuestRelationshipTypes = async () => {
    if (!wedding) return;
    const client = getAuthenticatedSupabase();
    await ensureDefaultGuestRelationshipTypes(client, wedding.id, locale === 'en' ? 'en' : 'cs');
    const { data, error } = await client
      .from('guest_relationship_types')
      .select('*')
      .eq('wedding_id', wedding.id)
      .order('sort_order');
    if (!error && data) setGuestRelTypes(data);
  };

  const addGuestRelType = async () => {
    if (!wedding || !newGuestRelLabel.trim()) return;
    const client = getAuthenticatedSupabase();
    const maxSort = guestRelTypes.reduce((m, x) => Math.max(m, x.sort_order), -1);
    const { error } = await client.from('guest_relationship_types').insert({
      wedding_id: wedding.id,
      label: newGuestRelLabel.trim(),
      sort_order: maxSort + 1,
    });
    if (error) {
      showToast('error', error.message);
      return;
    }
    setNewGuestRelLabel('');
    showToast('success', t('settings.guest_relationship_saved'));
    await loadGuestRelationshipTypes();
  };

  const updateGuestRelTypeLabel = async (id: string, label: string) => {
    const client = getAuthenticatedSupabase();
    const { error } = await client.from('guest_relationship_types').update({ label }).eq('id', id);
    if (error) {
      showToast('error', error.message);
      return;
    }
    showToast('success', t('settings.guest_relationship_saved'));
    await loadGuestRelationshipTypes();
  };

  const removeGuestRelType = async (id: string) => {
    const client = getAuthenticatedSupabase();
    const { error } = await client.from('guest_relationship_types').delete().eq('id', id);
    if (error) {
      if (error.code === '23503' || /foreign key|violates foreign key/i.test(error.message)) {
        showToast('error', t('settings.guest_relationship_delete_error'));
      } else {
        showToast('error', error.message);
      }
      return;
    }
    showToast('success', t('settings.guest_relationship_deleted'));
    setGuestRelTypes((prev) => prev.filter((x) => x.id !== id));
  };

  const loadWeddingMembers = async () => {
    if (!wedding) return;
    
    try {
      if (accessToken) {
        apiClient.setAuthToken(accessToken);
      }
      const client = getAuthenticatedSupabase();
      const { data: membersData, error: membersError } = await client
        .from('wedding_members')
        .select('user_id, role, role_key')
        .eq('wedding_id', wedding.id);

      if (membersError) {
        console.error('Error loading wedding members:', membersError);
        return;
      }

      if (!membersData || membersData.length === 0) {
        setMembers([]);
        return;
      }

      const userIds = [...new Set(membersData.map((m: { user_id: string }) => m.user_id))];
      const { data: profilesData, error: profilesError } = await client
        .from('profiles')
        .select('id, username, name, email')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error loading profiles for members:', profilesError);
        setMembers(membersData.map((m: any) => ({
          id: m.user_id,
          name: 'Neznámý uživatel',
          username: undefined,
          email: undefined,
          role: m.role,
          role_key: m.role_key,
        })));
        return;
      }

      const profilesById = (profilesData || []).reduce((acc: Record<string, any>, p: any) => {
        acc[p.id] = p;
        return acc;
      }, {});

      const formattedMembers = membersData.map((m: any) => {
        const profile = profilesById[m.user_id];
        return {
          id: m.user_id,
          name: profile?.name || profile?.username || 'Neznámý uživatel',
          username: profile?.username,
          email: profile?.email,
          role: m.role,
          role_key: m.role_key,
        };
      });

      setMembers(formattedMembers);
    } catch (err) {
      console.error('Error loading wedding members:', err);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;

    setIsCreatingUser(true);
    setMessage('');

    try {
      if (!accessToken) {
        throw new Error('Not authenticated');
      }
      apiClient.setAuthToken(accessToken);

      const result = await apiClient.createUser({
        username: newUserForm.username,
        email: newUserForm.email || undefined,
        password: newUserForm.password.trim() || undefined,
        wedding_id: wedding.id,
        role_key: newUserForm.role_key,
      });

      if (result.error) {
        showToast('error', result.error);
        setIsCreatingUser(false);
        return;
      }

      if (result.data) {
        setCreatedUserPassword(result.data.temp_password);
        showToast('success', 'Uživatel byl vytvořen.');
        setNewUserForm({
          username: '',
          email: '',
          password: '',
          role_key: 'FAMILY_GUEST',
        });
        await loadWeddingMembers();
      }
    } catch (err: any) {
      showToast('error', err.message || 'Nepodařilo se vytvořit uživatele');
    } finally {
      setIsCreatingUser(false);
    }
  };

  const closeAddUserModal = () => {
    setShowAddUserModal(false);
    setCreatedUserPassword(null);
    setMessage('');
  };

  const openEditMember = (member: Member) => {
    setEditMember(member);
    setEditForm({ role_key: member.role_key || 'FAMILY_GUEST', newPassword: '' });
  };

  const handleSaveEditMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding || !editMember) return;
    setIsSavingEdit(true);
    try {
      if (accessToken) apiClient.setAuthToken(accessToken);
      const client = getAuthenticatedSupabase();
      if (editForm.newPassword.trim().length >= 6 && editMember.id !== user?.id) {
        const res = await apiClient.adminResetPassword(editMember.id, editForm.newPassword.trim());
        if (res.error) {
          showToast('error', res.error);
          setIsSavingEdit(false);
          return;
        }
        showToast('success', 'Heslo bylo nastaveno.');
      }
      // Map role_key to old 'role' for backward compatibility
      const oldRoleMapping: Record<string, string> = {
        'WEDDING_ADMIN': 'admin',
        'COORDINATOR': 'coordinator',
        'BRIDE': 'viewer',
        'GROOM': 'viewer',
        'WITNESS': 'viewer',
        'BRIDESMAID': 'viewer',
        'FAMILY_GUEST': 'viewer'
      };
      const oldRole = oldRoleMapping[editForm.role_key] || 'viewer';
      
      const { error } = await client
        .from('wedding_members')
        .update({ role: oldRole, role_key: editForm.role_key })
        .eq('wedding_id', wedding.id)
        .eq('user_id', editMember.id);
      if (error) {
        showToast('error', error.message || 'Nepodařilo se uložit roli');
        setIsSavingEdit(false);
        return;
      }
      showToast('success', 'Úpravy uloženy.');
      setEditMember(null);
      await loadWeddingMembers();
    } catch (err: any) {
      showToast('error', err.message || 'Chyba při ukládání');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangePasswordMessage('');
    if (changePasswordForm.new !== changePasswordForm.confirm) {
      setChangePasswordMessage('Nové heslo a potvrzení se neshodují.');
      return;
    }
    if (changePasswordForm.new.length < 6) {
      setChangePasswordMessage('Nové heslo musí mít alespoň 6 znaků.');
      return;
    }
    if (!accessToken) {
      setChangePasswordMessage('Nejste přihlášeni.');
      return;
    }
    setIsChangingPassword(true);
    try {
      apiClient.setAuthToken(accessToken);
      const result = await apiClient.changePassword(changePasswordForm.current, changePasswordForm.new);
      if (result.error) {
        setChangePasswordMessage(result.error);
        return;
      }
      setChangePasswordMessage('Heslo bylo změněno.');
      setChangePasswordForm({ current: '', new: '', confirm: '' });
    } catch (err: any) {
      setChangePasswordMessage(err.message || 'Nepodařilo se změnit heslo.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const saveWedding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    setIsSaving(true);
    const vendorTypes = Array.isArray(weddingForm.vendor_types)
      ? [...weddingForm.vendor_types]
      : (Array.isArray(wedding.vendor_types) ? wedding.vendor_types : []);
    
    console.log('[Settings] Saving vendor_types:', vendorTypes);
    console.log('[Settings] accessToken available?', !!accessToken);
    
    if (!accessToken) {
      setMessage('Chyba: Nejste přihlášeni. Obnovte stránku a zkuste znovu.');
      setTimeout(() => setMessage(''), 5000);
      setIsSaving(false);
      return;
    }
    
    const payload = {
      title: weddingForm.title ?? wedding.title,
      date: weddingForm.date ?? wedding.date,
      location: weddingForm.location ?? wedding.location,
      default_language: weddingForm.default_language ?? wedding.default_language,
      vendor_types: vendorTypes,
    };
    
    apiClient.setAuthToken(accessToken);
    console.log('[Settings] Calling API with auth token');
    const { data: updated, error } = await apiClient.updateWedding(wedding.id, payload);
    
    console.log('[Settings] Update response:', { updated, error });
    
    if (!error && updated) {
      const fromDb = Array.isArray(updated.vendor_types) ? updated.vendor_types : [];
      const merged = {
        ...updated,
        vendor_types: fromDb.length >= vendorTypes.length ? fromDb : vendorTypes,
      };
      updateWeddingInList(wedding.id, merged);
      setMessage('Nastavení uloženo!');
      setTimeout(() => setMessage(''), 3000);
      await refreshWedding();
    } else if (error) {
      if (error.includes('Unauthorized') || error.includes('401')) {
        setMessage('Relace vypršela. Odhlaste se a přihlaste znovu.');
      } else {
        setMessage(`Chyba: ${error}`);
      }
      setTimeout(() => setMessage(''), 5000);
    }
    setIsSaving(false);
  };

  const handleUpgrade = async () => {
    if (!wedding) return;
    setIsSaving(true);
    const { error } = await (supabase as any).from('weddings').update({ plan: Plan.PRO, is_free_tier: false }).eq('id', wedding.id);
    if (!error) {
      setMessage('Plán byl úspěšně upgradován na PRO!');
      setTimeout(() => setMessage(''), 3000);
      refreshWedding();
    }
    setIsSaving(false);
  };

  const handleDowngrade = async () => {
    if (!wedding) return;
    setIsSaving(true);
    const { error } = await (supabase as any).from('weddings').update({ plan: Plan.FREE, is_free_tier: true }).eq('id', wedding.id);
    if (!error) {
      setMessage('Plán byl změněn na bezplatnou verzi. Po skončení akce budete mít omezený přístup.');
      setTimeout(() => setMessage(''), 4000);
      refreshWedding();
    }
    setIsSaving(false);
  };

  const addVendorType = () => {
    if (!newVendorType.trim()) return;
    const currentTypes = weddingForm.vendor_types || [];
    if (!currentTypes.includes(newVendorType.trim())) {
      setWeddingForm({ ...weddingForm, vendor_types: [...currentTypes, newVendorType.trim()] });
    }
    setNewVendorType('');
  };

  const removeVendorType = (typeToRemove: string) => {
    const currentTypes = weddingForm.vendor_types || [];
    setWeddingForm({ ...weddingForm, vendor_types: currentTypes.filter(t => t !== typeToRemove) });
  };

  const saveAiKey = (provider: AiProviderId, val: string) => {
    setApiKey(provider, val);
    setAiKeys(prev => ({ ...prev, [provider]: val }));
  };

  const saveCustomUrl = (val: string) => {
    setBaseUrl(val);
    setAiKeys(prev => ({ ...prev, customUrl: val }));
  };

  const toggleShowKey = (provider: string) => {
    setShowKeys(prev => ({ ...prev, [provider]: !prev[provider] }));
  };

  const testConnection = async () => {
    alert(t('ai.test_success')); // Mock test for UI demo
  };

  const saveInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    if (
      invForm.showGuestDetailsForm &&
      !invForm.guestFormShowAccommodation &&
      !invForm.guestFormShowAllergies &&
      !invForm.guestFormShowChildren &&
      !invForm.guestFormShowGuestNote
    ) {
      showToast(
        'error',
        'Dotazník: zapněte alespoň jednu oblast (ubytování, alergie, děti nebo poznámka), nebo vypněte celý blok.',
      );
      return;
    }
    setIsSavingInv(true);
    const publicConfig = {
      showSchedule: invForm.showSchedule,
      showFaq: invForm.showFaq,
      welcomeMessage: invForm.welcomeMessage,
      brideName: invForm.brideName || null,
      groomName: invForm.groomName || null,
      bridePhotoUrl: invForm.bridePhotoUrl || null,
      groomPhotoUrl: invForm.groomPhotoUrl || null,
      ceremonyTime: invForm.ceremonyTime || null,
      ceremonyVenue: invForm.ceremonyVenue || null,
      fullAddress: invForm.fullAddress || null,
      mapsUrl: invForm.mapsUrl || null,
      dressCode: invForm.dressCode || null,
      heroImageUrl: invForm.heroImageUrl || null,
      storyText: invForm.storyText || null,
      showCatering: invForm.showCatering,
      cateringIntro: invForm.cateringIntro.trim() || null,
      cateringShowMain: invForm.cateringShowMain,
      cateringMainText: invForm.cateringMainText.trim() || null,
      cateringShowWarmBuffet: invForm.cateringShowWarmBuffet,
      cateringWarmBuffetText: invForm.cateringWarmBuffetText.trim() || null,
      cateringShowColdBuffet: invForm.cateringShowColdBuffet,
      cateringColdBuffetText: invForm.cateringColdBuffetText.trim() || null,
      cateringShowSweetBar: invForm.cateringShowSweetBar,
      cateringSweetBarText: invForm.cateringSweetBarText.trim() || null,
      cateringShowDrinks: invForm.cateringShowDrinks,
      cateringDrinksText: invForm.cateringDrinksText.trim() || null,
      cateringMainPhotoUrl: invForm.cateringMainPhotoUrl.trim() || null,
      cateringWarmPhotoUrl: invForm.cateringWarmPhotoUrl.trim() || null,
      cateringColdPhotoUrl: invForm.cateringColdPhotoUrl.trim() || null,
      cateringSweetPhotoUrl: invForm.cateringSweetPhotoUrl.trim() || null,
      cateringDrinksPhotoUrl: invForm.cateringDrinksPhotoUrl.trim() || null,
      showMusic: invForm.showMusic,
      musicIntro: invForm.musicIntro.trim() || null,
      musicLiveDj: invForm.musicLiveDj,
      musicFromSpeakers: invForm.musicFromSpeakers,
      musicSpotifyUrl: invForm.musicSpotifyUrl.trim() || null,
      musicPlaylistCta: invForm.musicPlaylistCta.trim() || null,
      showEntertainment: invForm.showEntertainment,
      entertainmentIntro: null,
      showAccommodationInfo: invForm.showAccommodationInfo,
      accommodationInfo: invForm.accommodationInfo.trim() || null,
      accommodationMapsUrl: invForm.accommodationMapsUrl.trim() || null,
      showGuestDetailsForm: invForm.showGuestDetailsForm,
      guestFormIntro: invForm.guestFormIntro.trim() || null,
      guestFormShowAccommodation: invForm.guestFormShowAccommodation,
      guestFormShowAllergies: invForm.guestFormShowAllergies,
      guestFormShowChildren: invForm.guestFormShowChildren,
      guestFormShowGuestNote: invForm.guestFormShowGuestNote,
    };
    const { error } = await getAuthenticatedSupabase()
      .from('weddings')
      .update({
        public_enabled: invForm.public_enabled,
        public_slug: invForm.public_slug,
        welcome_message: invForm.welcomeMessage,
        public_config: publicConfig,
      })
      .eq('id', wedding.id);
    if (!error) {
      showToast('success', 'Nastavení pozvánky uloženo!');
      updateWeddingInList(wedding.id, {
        public_enabled: invForm.public_enabled,
        public_slug: invForm.public_slug,
        welcome_message: invForm.welcomeMessage,
        publicConfig: {
          showSchedule: publicConfig.showSchedule,
          showFaq: publicConfig.showFaq,
          welcomeMessage: publicConfig.welcomeMessage ?? '',
          brideName: publicConfig.brideName ?? undefined,
          groomName: publicConfig.groomName ?? undefined,
          bridePhotoUrl: publicConfig.bridePhotoUrl ?? undefined,
          groomPhotoUrl: publicConfig.groomPhotoUrl ?? undefined,
          ceremonyTime: publicConfig.ceremonyTime ?? undefined,
          ceremonyVenue: publicConfig.ceremonyVenue ?? undefined,
          fullAddress: publicConfig.fullAddress ?? undefined,
          mapsUrl: publicConfig.mapsUrl ?? undefined,
          dressCode: publicConfig.dressCode ?? undefined,
          heroImageUrl: publicConfig.heroImageUrl ?? undefined,
          storyText: publicConfig.storyText ?? undefined,
          showCatering: publicConfig.showCatering,
          cateringIntro: publicConfig.cateringIntro ?? undefined,
          cateringShowMain: publicConfig.cateringShowMain,
          cateringMainText: publicConfig.cateringMainText ?? undefined,
          cateringShowWarmBuffet: publicConfig.cateringShowWarmBuffet,
          cateringWarmBuffetText: publicConfig.cateringWarmBuffetText ?? undefined,
          cateringShowColdBuffet: publicConfig.cateringShowColdBuffet,
          cateringColdBuffetText: publicConfig.cateringColdBuffetText ?? undefined,
          cateringShowSweetBar: publicConfig.cateringShowSweetBar,
          cateringSweetBarText: publicConfig.cateringSweetBarText ?? undefined,
          cateringShowDrinks: publicConfig.cateringShowDrinks,
          cateringDrinksText: publicConfig.cateringDrinksText ?? undefined,
          cateringMainPhotoUrl: publicConfig.cateringMainPhotoUrl ?? undefined,
          cateringWarmPhotoUrl: publicConfig.cateringWarmPhotoUrl ?? undefined,
          cateringColdPhotoUrl: publicConfig.cateringColdPhotoUrl ?? undefined,
          cateringSweetPhotoUrl: publicConfig.cateringSweetPhotoUrl ?? undefined,
          cateringDrinksPhotoUrl: publicConfig.cateringDrinksPhotoUrl ?? undefined,
          showMusic: publicConfig.showMusic,
          musicIntro: publicConfig.musicIntro ?? undefined,
          musicLiveDj: publicConfig.musicLiveDj,
          musicFromSpeakers: publicConfig.musicFromSpeakers,
          musicSpotifyUrl: publicConfig.musicSpotifyUrl ?? undefined,
          musicPlaylistCta: publicConfig.musicPlaylistCta ?? undefined,
          showEntertainment: publicConfig.showEntertainment,
          entertainmentIntro: undefined,
          showAccommodationInfo: publicConfig.showAccommodationInfo,
          accommodationInfo: publicConfig.accommodationInfo ?? undefined,
          accommodationMapsUrl: publicConfig.accommodationMapsUrl ?? undefined,
          showGuestDetailsForm: publicConfig.showGuestDetailsForm,
          guestFormIntro: publicConfig.guestFormIntro ?? undefined,
          guestFormShowAccommodation: publicConfig.guestFormShowAccommodation,
          guestFormShowAllergies: publicConfig.guestFormShowAllergies,
          guestFormShowChildren: publicConfig.guestFormShowChildren,
          guestFormShowGuestNote: publicConfig.guestFormShowGuestNote,
        },
      });
      await refreshWedding();
    } else {
      showToast('error', `Chyba při ukládání: ${error.message}`);
    }
    setIsSavingInv(false);
  };

  const handleInvitationPhotoUpload = async (slot: InvitationPhotoSlot, file: File) => {
    if (!wedding) return;
    if (!isUsingRealSupabase) {
      showToast('error', 'Nahrávání fotek je v demo režimu nedostupné. Nastavte Supabase v .env.');
      return;
    }
    setInvUploadSlot(slot);
    try {
      const url = await uploadInvitationPublicImage(wedding.id, slot, file);
      setInvForm((f) => {
        switch (slot) {
          case 'bride':
            return { ...f, bridePhotoUrl: url };
          case 'groom':
            return { ...f, groomPhotoUrl: url };
          case 'hero':
            return { ...f, heroImageUrl: url };
          case 'cateringMain':
            return { ...f, cateringMainPhotoUrl: url };
          case 'cateringWarm':
            return { ...f, cateringWarmPhotoUrl: url };
          case 'cateringCold':
            return { ...f, cateringColdPhotoUrl: url };
          case 'cateringSweet':
            return { ...f, cateringSweetPhotoUrl: url };
          case 'cateringDrinks':
            return { ...f, cateringDrinksPhotoUrl: url };
          default:
            return f;
        }
      });
      showToast('success', 'Fotka nahrána. Uložte pozvánku, aby se změna projevila veřejně.');
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Chyba při nahrávání');
    } finally {
      setInvUploadSlot(null);
    }
  };

  const handleInvitationPhotoRemove = async (slot: InvitationPhotoSlot) => {
    const key =
      slot === 'bride'
        ? 'bridePhotoUrl'
        : slot === 'groom'
          ? 'groomPhotoUrl'
          : slot === 'hero'
            ? 'heroImageUrl'
            : slot === 'cateringMain'
              ? 'cateringMainPhotoUrl'
              : slot === 'cateringWarm'
                ? 'cateringWarmPhotoUrl'
                : slot === 'cateringCold'
                  ? 'cateringColdPhotoUrl'
                  : slot === 'cateringSweet'
                    ? 'cateringSweetPhotoUrl'
                    : 'cateringDrinksPhotoUrl';
    const current = invForm[key as keyof typeof invForm] as string;
    if (current && isUsingRealSupabase) {
      try {
        await removeInvitationPublicImageByUrl(current);
      } catch {
        // odkaz stejně vymažeme z pozvánky
      }
    }
    setInvForm((f) => ({ ...f, [key]: '' }));
  };

  const copyInvitationUrl = () => {
    const url = buildPublicInvitationUrl(window.location.origin, invForm.public_slug);
    navigator.clipboard.writeText(url).then(() => {
      setSlugCopied(true);
      setTimeout(() => setSlugCopied(false), 2000);
    });
  };

  if (!wedding) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        <div className="space-y-1">
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('nav.settings')}</h1>
          <p className="text-ds-body text-slate-500 dark:text-slate-400">Spravujte nastavení své svatby, předplatné a tým.</p>
        </div>

      {message && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-ds-input text-ds-body">
          {message}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
        {[
          { id: 'general', label: 'Obecné a vzhled' },
          { id: 'plan', label: 'Plán a platby' },
          { id: 'team', label: 'Tým a oprávnění' },
          { id: 'invitation', label: 'Pozvánka' },
          { id: 'ai', label: 'AI Integrace (BYOK)' }
        ].map(tab => (
          <button 
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`h-10 px-4 rounded-lg text-sm whitespace-nowrap transition-colors ${
              activeTab === tab.id 
              ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-medium' 
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* GENERAL TAB */}
      {activeTab === 'general' && (
        <div className="grid grid-cols-1 gap-6">
          <Card>
            <form onSubmit={saveWedding} className="space-y-6">
              <h2 className="text-ds-h2 text-slate-900 dark:text-white flex items-center gap-3">
                <Heart className="text-brand-500" size={20} /> {t('settings.wedding_info')}
              </h2>
              <div className="space-y-4">
                <Input 
                  label={t('settings.wedding_name_label')}
                  type="text" 
                  value={weddingForm.title || ''} 
                  onChange={e => setWeddingForm({...weddingForm, title: e.target.value})} 
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input 
                    label={t('settings.date_label')}
                    type="date" 
                    value={weddingForm.date || ''} 
                    onChange={e => setWeddingForm({...weddingForm, date: e.target.value})} 
                  />
                  <Input 
                    label={t('settings.location_label')}
                    type="text" 
                    value={weddingForm.location || ''} 
                    onChange={e => setWeddingForm({...weddingForm, location: e.target.value})} 
                  />
                </div>
              </div>

              {/* Vendor Types Section */}
              <div className="pt-6 border-t border-slate-200 dark:border-slate-700 space-y-4">
                 <h3 className="text-ds-h3 text-slate-900 dark:text-white flex items-center gap-2">
                    <List size={18} className="text-brand-500" /> {t('settings.vendor_categories_label')}
                 </h3>
                 <p className="text-ds-body text-slate-500 dark:text-slate-400">{t('settings.vendor_types_description')}</p>
                 
                 <div className="flex flex-wrap gap-2">
                    {weddingForm.vendor_types?.map(type => (
                       <Badge key={type} variant="default" className="flex items-center gap-2">
                          <span>{type}</span>
                          <button 
                            type="button"
                            onClick={() => removeVendorType(type)}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                          >
                             <X size={12} />
                          </button>
                       </Badge>
                    ))}
                 </div>

                 <div className="flex gap-2">
                    <Input 
                      value={newVendorType}
                      onChange={e => setNewVendorType(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addVendorType())}
                      placeholder="Nova kategorie (napr. Video)"
                      className="flex-1"
                    />
                    <Button 
                      type="button"
                      variant="secondary"
                      onClick={addVendorType}
                      icon={<Plus size={16} />}
                    >
                       Pridat
                    </Button>
                 </div>
              </div>

              <Button 
                type="submit" 
                disabled={isSaving}
                isLoading={isSaving}
                className="w-full"
              >
                {isSaving ? 'Ukladani...' : t('common.save')}
              </Button>
            </form>
          </Card>

          <Card>
            <h2 className="text-ds-h2 text-slate-900 dark:text-white flex items-center gap-3 mb-2">
              <Link2 className="text-brand-500" size={20} /> {t('settings.guest_relationship_types_title')}
            </h2>
            <p className="text-ds-body text-slate-500 dark:text-slate-400 mb-6">
              {t('settings.guest_relationship_types_description')}
            </p>
            <div className="space-y-3">
              {guestRelTypes.map((rt) => (
                <div key={`${rt.id}-${rt.label}`} className="flex gap-2 items-center">
                  <Input
                    defaultValue={rt.label}
                    className="flex-1"
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== rt.label) void updateGuestRelTypeLabel(rt.id, v);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => void removeGuestRelType(rt.id)}
                    className="p-2 text-slate-400 hover:text-red-500 rounded-lg shrink-0"
                    title={t('common.delete')}
                  >
                    <X size={18} />
                  </button>
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                <Input
                  value={newGuestRelLabel}
                  onChange={(e) => setNewGuestRelLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), void addGuestRelType())}
                  placeholder={t('settings.guest_relationship_new_placeholder')}
                  className="flex-1"
                />
                <Button type="button" variant="secondary" onClick={() => void addGuestRelType()} icon={<Plus size={16} />}>
                  {t('settings.guest_relationship_add')}
                </Button>
              </div>
            </div>
          </Card>

          <Card>
              <h2 className="text-ds-h2 text-slate-900 dark:text-white flex items-center gap-3 mb-6">
                <Languages className="text-brand-500" size={20} /> Jazyk a vzhled
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-3">Jazyk rozhraní</label>
                  <div className="flex gap-3">
                      <button 
                        type="button"
                        onClick={() => setLocale('cs')}
                        className={`flex-1 h-10 px-4 rounded-lg text-sm transition-colors ${
                          locale === 'cs' 
                            ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-medium' 
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                        }`}
                      >
                        Čeština
                      </button>
                      <button 
                        type="button"
                        onClick={() => setLocale('en')}
                        className={`flex-1 h-10 px-4 rounded-lg text-sm transition-colors ${
                          locale === 'en' 
                            ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-medium' 
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                        }`}
                      >
                        English
                      </button>
                  </div>
                </div>
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-3">Režim zobrazení</label>
                  <div className="flex gap-3">
                      <button 
                        type="button"
                        onClick={() => setTheme('light')}
                        className={`flex-1 h-10 px-4 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors ${
                          theme === 'light' 
                            ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-medium' 
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                        }`}
                      >
                        <Sun size={16} /> Světlý
                      </button>
                      <button 
                        type="button"
                        onClick={() => setTheme('dark')}
                        className={`flex-1 h-10 px-4 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors ${
                          theme === 'dark' 
                            ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-medium' 
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                        }`}
                      >
                        <Moon size={16} /> Tmavý
                      </button>
                  </div>
                </div>
              </div>
          </Card>
        </div>
      )}

      {/* PLAN TAB */}
      {activeTab === 'plan' && (() => {
        const effectivePro = wedding.plan === 'PRO' || ALL_USERS_PRO_PROMO;
        const downgradedButPromo = wedding.plan === 'FREE' && ALL_USERS_PRO_PROMO;
        return (
        <div className="space-y-8">
           <div className={`p-10 rounded-2xl shadow-ds-card ${effectivePro ? 'bg-slate-900 text-white' : 'bg-white dark:bg-slate-900'}`}>
              <div className="flex items-start justify-between">
                 <div>
                    <span className="bg-brand-500 text-white px-3 py-1 rounded-full text-ds-meta mb-4 inline-block">Váš aktuální plán</span>
                    <h2 className="text-4xl font-medium mb-2">{effectivePro ? 'MarryPlan PRO' : 'MarryPlan FREE'}</h2>
                    <p className={`${effectivePro ? 'text-slate-400' : 'text-slate-500'}`}>
                      {effectivePro
                        ? 'Máte přístup ke všem funkcím bez omezení.'
                        : 'Základní verze s omezením počtu hostů a funkcí.'}
                    </p>
                    {ALL_USERS_PRO_PROMO && (
                      <p className="mt-3 text-sm text-slate-400">
                        Aktuálně běží akce – všichni mají přístup k Pro. Můžete se kdykoli přejít na bezplatnou verzi.
                      </p>
                    )}
                    {downgradedButPromo && (
                      <p className="mt-3 text-sm text-amber-400">
                        Zvolili jste bezplatnou verzi. Po skončení akce budete mít omezený přístup (rozpočet, šablony, zasedací pořádek).
                      </p>
                    )}
                 </div>
                 {effectivePro ? <CheckCircle className="text-green-500" size={48} /> : <AlertTriangle className="text-amber-500" size={48} />}
              </div>

              {effectivePro && wedding.plan === 'PRO' && (
                <div className="mt-8 pt-8 border-t border-slate-700">
                   <button
                     type="button"
                     onClick={handleDowngrade}
                     className="w-full py-3 rounded-2xl border-2 border-slate-500 text-slate-300 hover:bg-slate-800 hover:border-slate-400 transition-colors"
                   >
                     Přejít na bezplatnou verzi
                   </button>
                </div>
              )}

              {!effectivePro && (
                <div className="mt-8 pt-8 border-t border-slate-100 dark:border-slate-800">
                   <h3 className="mb-4">Proč upgradovat?</h3>
                   <ul className="space-y-3 mb-8">
                      <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} className="text-green-500" /> Neomezený počet hostů</li>
                      <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} className="text-green-500" /> Pokročilé sledování rozpočtu</li>
                      <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} className="text-green-500" /> Tvorba zasedacího pořádku</li>
                      <li className="flex items-center gap-3 text-sm"><CheckCircle size={16} className="text-green-500" /> Export dat a tisk</li>
                   </ul>
                   <button 
                    type="button"
                    onClick={handleUpgrade}
                    className="w-full bg-gradient-to-r from-brand-500 to-pink-600 text-white py-4 rounded-2xl font-medium text-lg shadow-xl shadow-brand-500/30 hover:scale-[1.02] transition-transform"
                   >
                     Upgradovat na PRO (299 Kč / měsíc)
                   </button>
                </div>
              )}
           </div>
           
           <div className="bg-white dark:bg-slate-800/50 p-6 rounded-2xl shadow-ds-card flex items-center justify-between">
              <div className="flex items-center gap-4">
                 <CreditCard className="text-slate-400" />
                 <div>
                    <p className="font-bold text-slate-900 dark:text-white">•••• •••• •••• 4242</p>
                    <p className="text-xs text-slate-500">Expirace 12/28</p>
                 </div>
              </div>
              <button type="button" className="text-brand-600 text-sm font-bold hover:underline">Změnit kartu</button>
           </div>
        </div>
        );
      })()}

      {/* TEAM TAB */}
      {activeTab === 'team' && (
        <div className="bg-white dark:bg-slate-900 p-10 rounded-2xl shadow-ds-card space-y-8">
            {/* Uživatelé – tabulka + Přidat */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-ds-h2 text-slate-900 dark:text-white flex items-center gap-3">
                  <Users className="text-brand-500" /> Uživatelé
                </h2>
                <Button
                  type="button"
                  onClick={() => setShowAddUserModal(true)}
                  variant="secondary"
                  icon={<PlusCircle size={18} />}
                >
                  {t('common.add')}
                </Button>
              </div>

              {members.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400 rounded-2xl border border-ds-border">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Zatím nejsou žádní členové. Přidejte uživatele tlačítkem výše.</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-ds-border/50 shadow-ds-card">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-slate-50 dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800">
                        <th className="px-4 py-3 text-ds-meta text-slate-400">Uživatel</th>
                        <th className="px-4 py-3 text-ds-meta text-slate-400">Email</th>
                        <th className="px-4 py-3 text-ds-meta text-slate-400">Role</th>
                        <th className="px-4 py-3 text-ds-meta text-slate-400 w-24">Akce</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((member) => (
                        <tr key={member.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                          <td className="px-4 py-3">
                            <span className="font-bold text-slate-900 dark:text-white">{member.name || member.username || '—'}</span>
                            {member.username && <span className="block text-xs text-slate-500 font-mono">@{member.username}</span>}
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{member.email || '—'}</td>
                          <td className="px-4 py-3">
                            <span className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded text-ds-meta">
                              {WEDDING_ROLES.find(r => r.key === member.role_key)?.label || member.role || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button type="button" onClick={() => openEditMember(member)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-colors" title="Upravit">
                              <Pencil size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Změna vlastního hesla – oddělená sekce */}
            <div className="p-6 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-ds-border space-y-4">
              <h3 className="text-ds-h3 font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Lock size={18} /> Změna vlastního hesla
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Jste přihlášeni jako <strong className="text-slate-700 dark:text-slate-300">{user?.email || (user as any)?.username || 'uživatel'}</strong>. Pro změnu hesla zadejte současné heslo a nové heslo. Žádný e‑mail ani OTP.
              </p>
              {changePasswordMessage && (
                <div className={`p-3 rounded-xl text-sm ${changePasswordMessage.includes('změněno') ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'}`}>
                  {changePasswordMessage}
                </div>
              )}
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Současné heslo *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="password"
                      value={changePasswordForm.current}
                      onChange={(e) => setChangePasswordForm({ ...changePasswordForm, current: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500"
                      placeholder="Současné heslo"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Nové heslo *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="password"
                      value={changePasswordForm.new}
                      onChange={(e) => setChangePasswordForm({ ...changePasswordForm, new: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500"
                      placeholder="Min. 6 znaků"
                      minLength={6}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Potvrzení nového hesla *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="password"
                      value={changePasswordForm.confirm}
                      onChange={(e) => setChangePasswordForm({ ...changePasswordForm, confirm: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500"
                      placeholder="Znovu nové heslo"
                      minLength={6}
                      required
                    />
                  </div>
                </div>
                <button type="submit" disabled={isChangingPassword} className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-3 rounded-xl font-medium transition-colors disabled:opacity-50">
                  {isChangingPassword ? 'Ukládám...' : 'Změnit heslo'}
                </button>
              </form>
            </div>

            {/* Roles Info Panel */}
            <div className="border border-ds-border rounded-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowRolesInfo(!showRolesInfo)}
                className="w-full p-4 bg-slate-50 dark:bg-slate-950 flex items-center justify-between hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors"
              >
                <span className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Info size={18} className="text-brand-500" /> Přehled rolí a oprávnění
                </span>
                {showRolesInfo ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
              </button>
              
              {showRolesInfo && (
                <div className="p-4 space-y-4 bg-white dark:bg-slate-900">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Každá role má definovaná oprávnění pro čtení a zápis v jednotlivých sekcích aplikace.
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-800">
                          <th className="text-left py-2 px-3 text-ds-meta text-slate-400">Role</th>
                          <th className="text-left py-2 px-3 text-ds-meta text-slate-400">Může číst</th>
                          <th className="text-left py-2 px-3 text-ds-meta text-slate-400">Může upravovat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {WEDDING_ROLES.map(role => {
                          const perms = ROLE_PERMISSIONS_MATRIX[role.key as keyof typeof ROLE_PERMISSIONS_MATRIX];
                          return (
                            <tr key={role.key} className="border-b border-slate-50 dark:border-slate-800 last:border-0">
                              <td className="py-3 px-3">
                                <span className="font-bold text-slate-900 dark:text-white">{role.label}</span>
                                {perms?.canManageUsers && (
                                  <span className="ml-2 text-[9px] bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400 px-1.5 py-0.5 rounded-full font-bold uppercase">
                                    správa uživatelů
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-3 text-slate-600 dark:text-slate-400">
                                {perms?.canRead?.join(', ') || '—'}
                              </td>
                              <td className="py-3 px-3 text-slate-600 dark:text-slate-400">
                                {perms?.canWrite?.length ? perms.canWrite.join(', ') : <span className="text-slate-400">Pouze čtení</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/10 rounded-xl text-blue-800 dark:text-blue-400 text-xs">
                    <strong>Tip:</strong> SUPER_ADMIN (platformový administrátor) má automaticky plný přístup ke všem svatbám bez ohledu na přiřazenou roli.
                  </div>
                </div>
              )}
            </div>
        </div>
      )}

      {/* Modal: Přidat uživatele */}
      <Modal isOpen={showAddUserModal} onClose={closeAddUserModal} title={createdUserPassword ? 'Uživatel vytvořen' : 'Přidat uživatele'} maxWidth="max-w-lg">
        {createdUserPassword ? (
          <div className="space-y-4">
            <p className="text-slate-600 dark:text-slate-400">Úvodní heslo předajte uživateli. Si ho může změnit v Nastavení → Změna vlastního hesla.</p>
            <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl">
              <p className="text-ds-meta text-slate-500 dark:text-slate-400 mb-1">Úvodní heslo</p>
              <p className="font-mono font-bold text-slate-900 dark:text-white break-all">{createdUserPassword}</p>
            </div>
            <button type="button" onClick={closeAddUserModal} className="w-full bg-brand-600 hover:bg-brand-700 text-white py-3 rounded-xl font-bold">
              Zavřít
            </button>
          </div>
        ) : (
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Uživatelské jméno *</label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input type="text" required value={newUserForm.username} onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value.toLowerCase().trim() })} className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500" placeholder="např. jan_novak" pattern="[a-z0-9_-]{3,30}" title="3-30 znaků" />
              </div>
            </div>
            <div>
              <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Úvodní heslo *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input type="password" value={newUserForm.password} onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })} className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500" placeholder="Min. 6 znaků" minLength={6} required />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Uživatel si heslo může později změnit v Nastavení.</p>
            </div>
            <div>
              <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Email (volitelné)</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input type="email" value={newUserForm.email} onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })} className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500" placeholder="jan@example.com" />
              </div>
            </div>
            <div>
              <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Role ve svatbě</label>
              <select value={newUserForm.role_key} onChange={(e) => setNewUserForm({ ...newUserForm, role_key: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500">
                {WEDDING_ROLES.map(role => (
                  <option key={role.key} value={role.key}>{role.label}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {WEDDING_ROLES.find(r => r.key === newUserForm.role_key)?.description}
              </p>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={isCreatingUser} className="flex-1 bg-brand-600 hover:bg-brand-700 text-white py-3 rounded-xl font-bold disabled:opacity-50">{isCreatingUser ? 'Vytváření...' : 'Vytvořit uživatele'}</button>
              <button type="button" onClick={closeAddUserModal} className="px-6 py-3 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white rounded-xl font-bold hover:bg-slate-300 dark:hover:bg-slate-600">Zrušit</button>
            </div>
          </form>
        )}
      </Modal>

      {/* Modal: Upravit uživatele */}
      <Modal isOpen={!!editMember} onClose={() => setEditMember(null)} title="Upravit uživatele" maxWidth="max-w-lg">
        {editMember && (
          <form onSubmit={handleSaveEditMember} className="space-y-4">
            <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-xl text-sm">
              <p className="font-bold text-slate-900 dark:text-white">{editMember.name || editMember.username || '—'}</p>
              {editMember.username && <p className="font-mono text-slate-500">@{editMember.username}</p>}
              {editMember.email && <p className="text-slate-500">{editMember.email}</p>}
            </div>
            <div>
              <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Role ve svatbě</label>
              <select value={editForm.role_key} onChange={(e) => setEditForm({ ...editForm, role_key: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500">
                {WEDDING_ROLES.map(role => (
                  <option key={role.key} value={role.key}>{role.label}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {WEDDING_ROLES.find(r => r.key === editForm.role_key)?.description}
              </p>
            </div>
            {editMember.id !== user?.id && (
              <div>
                <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Nastavit nové heslo (volitelné)</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input type="password" value={editForm.newPassword} onChange={(e) => setEditForm({ ...editForm, newPassword: e.target.value })} className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500" placeholder="Min. 6 znaků – ponechte prázdné, nechcete-li měnit" minLength={6} />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Pouze správce může nastavit heslo jinému uživateli bez znalosti současného hesla.</p>
              </div>
            )}
            <div className="flex gap-3">
              <button type="submit" disabled={isSavingEdit} className="flex-1 bg-brand-600 hover:bg-brand-700 text-white py-3 rounded-xl font-bold disabled:opacity-50">{isSavingEdit ? 'Ukládám...' : 'Uložit'}</button>
              <button type="button" onClick={() => setEditMember(null)} className="px-6 py-3 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white rounded-xl font-bold hover:bg-slate-300 dark:hover:bg-slate-600">Zrušit</button>
            </div>
          </form>
        )}
      </Modal>

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}

      {/* INVITATION TAB */}
      {activeTab === 'invitation' && (
        <div className="space-y-6">
          <Card>
            <form onSubmit={saveInvitation} className="space-y-6">
              <h2 className="text-ds-h2 text-slate-900 dark:text-white flex items-center gap-3">
                <Heart className="text-brand-500" size={20} /> Veřejná pozvánka
              </h2>
              {!isUsingRealSupabase && (
                <p className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 rounded-xl px-4 py-3 border border-amber-200/80 dark:border-amber-800/80">
                  Nahrávání fotek funguje jen s reálným projektem Supabase (v demo režimu nejsou k dispozici).
                </p>
              )}

              {/* Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl">
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">Zapnout veřejnou pozvánku</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Hosté si mohou zobrazit pozvánku bez přihlášení.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setInvForm(f => ({ ...f, public_enabled: !f.public_enabled }))}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${invForm.public_enabled ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600'}`}
                >
                  <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${invForm.public_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* Slug + URL */}
              <div className="space-y-2">
                <label className="text-ds-meta text-slate-500 dark:text-slate-400 block">URL pozvánky (slug)</label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={invForm.public_slug}
                    onChange={e => setInvForm(f => ({ ...f, public_slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') }))}
                    placeholder="jana-a-petr-2025"
                    className="flex-1"
                  />
                  <Button type="button" variant="secondary" onClick={copyInvitationUrl} disabled={!invForm.public_slug}>
                    {slugCopied ? 'Zkopírováno!' : 'Kopírovat URL'}
                  </Button>
                </div>
                {invForm.public_slug && (
                  <p className="text-xs text-slate-400 font-mono break-all">
                    {buildPublicInvitationUrl(window.location.origin, invForm.public_slug)}
                  </p>
                )}
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
                <h3 className="text-ds-h3 text-slate-900 dark:text-white">Pár</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Jméno nevěsty" type="text" value={invForm.brideName} onChange={e => setInvForm(f => ({ ...f, brideName: e.target.value }))} placeholder="Jana Nováková" />
                  <Input label="Jméno ženicha" type="text" value={invForm.groomName} onChange={e => setInvForm(f => ({ ...f, groomName: e.target.value }))} placeholder="Petr Novák" />
                  <InvitationPhotoUpload
                    label="Foto nevěsty"
                    value={invForm.bridePhotoUrl}
                    uploading={invUploadSlot === 'bride'}
                    disabled={!isUsingRealSupabase}
                    onFileSelected={(file) => handleInvitationPhotoUpload('bride', file)}
                    onRemove={() => void handleInvitationPhotoRemove('bride')}
                  />
                  <InvitationPhotoUpload
                    label="Foto ženicha"
                    value={invForm.groomPhotoUrl}
                    uploading={invUploadSlot === 'groom'}
                    disabled={!isUsingRealSupabase}
                    onFileSelected={(file) => handleInvitationPhotoUpload('groom', file)}
                    onRemove={() => void handleInvitationPhotoRemove('groom')}
                  />
                </div>
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
                <h3 className="text-ds-h3 text-slate-900 dark:text-white">Obřad a místo</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Čas obřadu" type="text" value={invForm.ceremonyTime} onChange={e => setInvForm(f => ({ ...f, ceremonyTime: e.target.value }))} placeholder="16:00" />
                  <Input label="Název místa" type="text" value={invForm.ceremonyVenue} onChange={e => setInvForm(f => ({ ...f, ceremonyVenue: e.target.value }))} placeholder="Zámek Kozel" />
                </div>
                <Input label="Plná adresa" type="text" value={invForm.fullAddress} onChange={e => setInvForm(f => ({ ...f, fullAddress: e.target.value }))} placeholder="Kozel 1, 335 44 Šťáhlavy" />
                <Input
                  label="Google Maps URL"
                  type="url"
                  value={invForm.mapsUrl}
                  onChange={(e) => setInvForm((f) => ({ ...f, mapsUrl: e.target.value }))}
                  placeholder="Odkaz „Sdílet“ z Map (špendlík) — přesněji zaměří vloženou mapu na pozvánce"
                />
                <Input label="Dress code" type="text" value={invForm.dressCode} onChange={e => setInvForm(f => ({ ...f, dressCode: e.target.value }))} placeholder="Elegantní formální oděv" />
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
                <h3 className="text-ds-h3 text-slate-900 dark:text-white">Obsah a fotografie</h3>
                <InvitationPhotoUpload
                  label="Hero fotografie (pozadí záhlaví)"
                  value={invForm.heroImageUrl}
                  uploading={invUploadSlot === 'hero'}
                  disabled={!isUsingRealSupabase}
                  previewClassName="w-full max-w-md h-32"
                  onFileSelected={(file) => handleInvitationPhotoUpload('hero', file)}
                  onRemove={() => void handleInvitationPhotoRemove('hero')}
                />
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Uvítací zpráva</label>
                  <textarea
                    value={invForm.welcomeMessage}
                    onChange={e => setInvForm(f => ({ ...f, welcomeMessage: e.target.value }))}
                    rows={3}
                    placeholder="Vážení přátelé, rádi bychom vás pozvali..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  />
                </div>
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Příběh páru (volitelné)</label>
                  <textarea
                    value={invForm.storyText}
                    onChange={e => setInvForm(f => ({ ...f, storyText: e.target.value }))}
                    rows={4}
                    placeholder="Jak jsme se poznali..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  />
                </div>
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
                <h3 className="text-ds-h3 text-slate-900 dark:text-white">Občerstvení na pozvánce</h3>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={invForm.showCatering}
                    onChange={(e) => setInvForm((f) => ({ ...f, showCatering: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">Zobrazit sekci občerstvení</span>
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Sekce se na pozvánce objeví jen pokud je zapnutá a máte zaškrtnutý alespoň jeden dílčí blok níže. Prázdný text u bloku použije výchozí krátký popis na pozvánce. Na pozvánce se zachovají konce řádků; řádek začínající pomlčkou a mezerou (např.{' '}
                  <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded">- položka</code>) se zobrazí jako odrážka, na jednom řádku lze psát{' '}
                  <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded">- a - b - c</code>.
                </p>
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Úvod k občerstvení (volitelné)</label>
                  <textarea
                    value={invForm.cateringIntro}
                    onChange={(e) => setInvForm((f) => ({ ...f, cateringIntro: e.target.value }))}
                    rows={2}
                    placeholder={CATERING_SECTION_INTRO_DEFAULT}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                  />
                </div>
                <div className="space-y-4 pl-1 border-l-2 border-slate-200 dark:border-slate-600 pl-4">
                  {INVITATION_CATERING_BLOCKS.map(({ showKey, textKey, photoKey, slot, label, placeholder }) => (
                    <div key={showKey} className="space-y-2">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={invForm[showKey]}
                          onChange={(e) => setInvForm((f) => ({ ...f, [showKey]: e.target.checked }))}
                          className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        />
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</span>
                      </label>
                      <textarea
                        value={invForm[textKey]}
                        onChange={(e) => setInvForm((f) => ({ ...f, [textKey]: e.target.value }))}
                        rows={2}
                        disabled={!invForm[showKey]}
                        placeholder={placeholder}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-none disabled:opacity-50"
                      />
                      <InvitationPhotoUpload
                        label={`Foto — ${label} (dolní polovina dlaždice na pozvánce)`}
                        value={invForm[photoKey]}
                        uploading={invUploadSlot === slot}
                        disabled={!isUsingRealSupabase || !invForm[showKey]}
                        previewClassName="w-full max-w-xs h-28"
                        onFileSelected={(file) => void handleInvitationPhotoUpload(slot, file)}
                        onRemove={() => void handleInvitationPhotoRemove(slot)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
                <h3 className="text-ds-h3 text-slate-900 dark:text-white">Hudba na pozvánce</h3>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={invForm.showMusic}
                    onChange={(e) => setInvForm((f) => ({ ...f, showMusic: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">Zobrazit sekci hudba</span>
                </label>
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Úvod (volitelné)</label>
                  <textarea
                    value={invForm.musicIntro}
                    onChange={(e) => setInvForm((f) => ({ ...f, musicIntro: e.target.value }))}
                    rows={2}
                    disabled={!invForm.showMusic}
                    placeholder="Večer doplníme hudbu tak, aby…"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-none disabled:opacity-50"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-ds-meta text-slate-500 dark:text-slate-400">Forma hudby (lze obojí)</span>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invForm.musicLiveDj}
                      onChange={(e) => setInvForm((f) => ({ ...f, musicLiveDj: e.target.checked }))}
                      disabled={!invForm.showMusic}
                      className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Živý DJ</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invForm.musicFromSpeakers}
                      onChange={(e) => setInvForm((f) => ({ ...f, musicFromSpeakers: e.target.checked }))}
                      disabled={!invForm.showMusic}
                      className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Hudba z reproduktorů / ozvučení</span>
                  </label>
                </div>
                <Input
                  label="Odkaz na Spotify playlist (volitelné)"
                  type="url"
                  value={invForm.musicSpotifyUrl}
                  onChange={(e) => setInvForm((f) => ({ ...f, musicSpotifyUrl: e.target.value }))}
                  disabled={!invForm.showMusic}
                  placeholder="https://open.spotify.com/playlist/…"
                />
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">
                    Výzva k přidání skladeb do playlistu (volitelné)
                  </label>
                  <textarea
                    value={invForm.musicPlaylistCta}
                    onChange={(e) => setInvForm((f) => ({ ...f, musicPlaylistCta: e.target.value }))}
                    rows={3}
                    disabled={!invForm.showMusic}
                    placeholder="Připravili jsme společný playlist…"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-none disabled:opacity-50"
                  />
                </div>
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
                <h3 className="text-ds-h3 text-slate-900 dark:text-white">Informace pro ubytované</h3>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={invForm.showAccommodationInfo}
                    onChange={(e) => setInvForm((f) => ({ ...f, showAccommodationInfo: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">Zobrazit sekci na pozvánce</span>
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Volitelná sekce pro hosty, kteří přespávají &mdash; název hotelu, časy check-inu, doprava na místo apod.
                  Konce řádků se zachovají; řádek začínající <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded">- </code> se zobrazí jako odrážka.
                </p>
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Text pro ubytované</label>
                  <textarea
                    value={invForm.accommodationInfo}
                    onChange={(e) => setInvForm((f) => ({ ...f, accommodationInfo: e.target.value }))}
                    rows={4}
                    disabled={!invForm.showAccommodationInfo}
                    placeholder={ACCOMMODATION_INFO_DEFAULT}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-none disabled:opacity-50"
                  />
                </div>
                <Input
                  label="Odkaz na adresu hotelu (Google Maps, volitelné)"
                  type="url"
                  value={invForm.accommodationMapsUrl}
                  onChange={(e) => setInvForm((f) => ({ ...f, accommodationMapsUrl: e.target.value }))}
                  disabled={!invForm.showAccommodationInfo}
                  placeholder="https://maps.google.com/?q=Baltaci+Hotel+U+Náhonu+Zlín"
                />
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
                <h3 className="text-ds-h3 text-slate-900 dark:text-white">Dotazník na konci pozvánky</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Host vyplní jméno a příjmení; odpovědi uvidíte v sekci <strong>Hosté</strong>. Jedna společná pozvánka pro všechny.
                </p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={invForm.showGuestDetailsForm}
                    onChange={(e) => setInvForm((f) => ({ ...f, showGuestDetailsForm: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">Zobrazit rozbalovací dotazník (ke konci stránky)</span>
                </label>
                <div>
                  <label className="text-ds-meta text-slate-500 dark:text-slate-400 block mb-2">Úvodní text (volitelné)</label>
                  <textarea
                    value={invForm.guestFormIntro}
                    onChange={(e) => setInvForm((f) => ({ ...f, guestFormIntro: e.target.value }))}
                    rows={2}
                    disabled={!invForm.showGuestDetailsForm}
                    placeholder="Budeme rádi za pár informací v přípravě na svatbu…"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-none disabled:opacity-50"
                  />
                </div>
                <div className="space-y-2 pl-1">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invForm.guestFormShowAccommodation}
                      onChange={(e) => setInvForm((f) => ({ ...f, guestFormShowAccommodation: e.target.checked }))}
                      disabled={!invForm.showGuestDetailsForm}
                      className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Zájem o ubytování (ano / ne / ještě nevím)</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invForm.guestFormShowAllergies}
                      onChange={(e) => setInvForm((f) => ({ ...f, guestFormShowAllergies: e.target.checked }))}
                      disabled={!invForm.showGuestDetailsForm}
                      className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Alergie / dietní omezení</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invForm.guestFormShowChildren}
                      onChange={(e) => setInvForm((f) => ({ ...f, guestFormShowChildren: e.target.checked }))}
                      disabled={!invForm.showGuestDetailsForm}
                      className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Děti (ano nebo ještě nevím + počet)</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invForm.guestFormShowGuestNote}
                      onChange={(e) => setInvForm((f) => ({ ...f, guestFormShowGuestNote: e.target.checked }))}
                      disabled={!invForm.showGuestDetailsForm}
                      className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Poznámka (volitelný text)</span>
                  </label>
                </div>
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
                <h3 className="text-ds-h3 text-slate-900 dark:text-white">Zobrazit na pozvánce</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invForm.showSchedule}
                      onChange={e => setInvForm(f => ({ ...f, showSchedule: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Harmonogram dne</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invForm.showEntertainment}
                      onChange={(e) => setInvForm((f) => ({ ...f, showEntertainment: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Program a zábava</span>
                  </label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 pl-7">
                    Zobrazí schválené aktivity z oddílu <strong>Zábava</strong> v aplikaci (bez hlasování).
                  </p>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={invForm.showFaq}
                      onChange={e => setInvForm(f => ({ ...f, showFaq: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Důležité informace (FAQ)</span>
                  </label>
                </div>
              </div>

              <Button type="submit" disabled={isSavingInv} isLoading={isSavingInv} className="w-full">
                {isSavingInv ? 'Ukládání...' : 'Uložit pozvánku'}
              </Button>
            </form>
          </Card>
        </div>
      )}

      {/* AI TAB */}
      {activeTab === 'ai' && (
        <div className="bg-white dark:bg-slate-900 p-10 rounded-2xl border border-ds-border space-y-8">
           <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-brand-600 text-white rounded-2xl">
                 <Bot size={24} />
              </div>
              <div>
                 <h2 className="text-ds-h2 text-slate-900 dark:text-white">{t('ai.settings_title')}</h2>
                 <p className="text-slate-500 dark:text-slate-400">{t('ai.settings_desc')}</p>
              </div>
           </div>

           <div className="space-y-6">
              {['openai', 'anthropic', 'gemini', 'custom'].map((provider) => (
                <div key={provider} className="p-6 bg-slate-50 dark:bg-slate-950 rounded-3xl border border-ds-border space-y-4">
                   <div className="flex items-center justify-between">
                      <h3 className="text-ds-h3 font-semibold capitalize">{provider}</h3>
                      <button onClick={testConnection} className="text-xs font-medium text-brand-600 hover:underline flex items-center gap-1">
                         <Wifi size={14} /> {t('ai.test_connection')}
                      </button>
                   </div>
                   
                   {provider === 'custom' && (
                     <div>
                       <label className="text-ds-meta text-slate-400 block mb-2 px-1">Base URL</label>
                       <input 
                         type="text" 
                         value={aiKeys.customUrl}
                         onChange={(e) => saveCustomUrl(e.target.value)}
                         className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                         placeholder="https://api.example.com/v1"
                       />
                     </div>
                   )}

                   <div>
                      <label className="text-ds-meta text-slate-400 block mb-2 px-1">API Key</label>
                      <div className="relative">
                         <input 
                           type={showKeys[provider] ? 'text' : 'password'}
                           value={aiKeys[provider] || ''}
                           onChange={(e) => saveAiKey(provider as AiProviderId, e.target.value)}
                           className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-brand-500 outline-none pr-12 font-mono"
                           placeholder="sk-..."
                         />
                         <button 
                           onClick={() => toggleShowKey(provider)}
                           className="absolute right-4 top-3 text-slate-400 hover:text-slate-600"
                         >
                            {showKeys[provider] ? <EyeOff size={18} /> : <Eye size={18} />}
                         </button>
                      </div>
                   </div>
                </div>
              ))}
           </div>

           <div className="p-4 bg-amber-50 dark:bg-amber-900/10 rounded-2xl text-xs text-amber-800 dark:text-amber-500 font-medium flex gap-3">
              <Lock size={16} className="shrink-0" />
              <p>Bezpečnostní upozornění: API klíče jsou uloženy pouze v LocalStorage vašeho prohlížeče. Nikdy se neposílají na náš server. Při odhlášení nebo vymazání dat prohlížeče budou klíče ztraceny.</p>
           </div>
        </div>
      )}
    </div>
  );
}
